/**
 * The itemised fare shown on every rider-facing receipt, built once so the live
 * trip screen, the history receipt and the ride-details page can never disagree —
 * and so the rows always add up to the total.
 *
 * Input is the object from `getFareSnapshot(ride)`. Row order:
 *   base · distance · minimum · tip · night · rain · cancellation · waiting · other
 * Components the snapshot doesn't carry (older rides) are covered by a final
 * "other" row, so a receipt never shows rows that fail to add up to its total.
 */
const money = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export const fareRows = (snapshot) => {
  if (!snapshot || !Number.isFinite(Number(snapshot.total))) return { rows: [], total: null, sum: 0 };
  const rows = [];
  const push = (id, amount, extra = {}) => rows.push({ id, amount, ...extra });

  if (snapshot.baseFare != null) push('base', money(snapshot.baseFare));
  if (snapshot.distanceCharge != null) push('distance', money(snapshot.distanceCharge));
  if (money(snapshot.minimumAdjustment) > 0) push('minimum', money(snapshot.minimumAdjustment));
  if (money(snapshot.tip) > 0) push('tip', money(snapshot.tip));
  if (snapshot.isNightFare === true && money(snapshot.nightSurcharge) > 0) push('night', money(snapshot.nightSurcharge));
  if (snapshot.isRainFare === true && money(snapshot.rainSurcharge) > 0) push('rain', money(snapshot.rainSurcharge));
  if (money(snapshot.cancellationFee) > 0) push('cancellation', money(snapshot.cancellationFee));
  if (money(snapshot.waitingCharge) > 0) push('waiting', money(snapshot.waitingCharge), { minutes: money(snapshot.waitingMinutes) });

  const total = Number(snapshot.total);
  const sum = rows.reduce((s, r) => s + r.amount, 0);
  // Only ever ADD a remainder row: a negative gap would mean the snapshot is
  // inconsistent and hiding it behind a minus row would be worse than leaving it.
  if (total - sum > 0) push('other', total - sum);
  return { rows, total, sum: rows.reduce((s, r) => s + r.amount, 0) };
};

/**
 * The label for a row. `overrides[rowId]` may be an i18n key (to keep a surface's
 * existing wording for that row) or a function (row) => string for labels that
 * need extra values.
 */
export const fareRowLabel = (row, t, overrides = {}) => {
  const override = overrides[row.id];
  if (typeof override === 'function') return override(row);
  const text = t(override || FARE_ROW_LABEL_KEYS[row.id], row.id === 'waiting' ? { min: row.minutes } : undefined);
  if (row.id === 'night') return `🌙 ${text}`;
  if (row.id === 'rain') return `🌧️ ${text}`;
  return text;
};

/** Default i18n key for each row (a surface may override base/distance to keep its own wording). */
export const FARE_ROW_LABEL_KEYS = {
  base: 'fare.base',
  distance: 'fare.distance',
  minimum: 'book.fareMinimum',
  tip: 'fare.tip',
  night: 'fare.nightSurcharge',
  rain: 'fare.rainSurcharge',
  cancellation: 'fare.cancellationFee',
  waiting: 'fare.waiting',
  other: 'fare.otherAdjustments',
};
